import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Box, Button, Grid, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Joi from '@hapi/joi';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import CustomTextField from 'components/Forms/TextField';
import CustomSelect from 'components/Forms/CustomSelect';
import CustomDatePicker from 'components/Forms/CustomDatePicker';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import { resolveApiErrorMessage } from 'utils';
import { canLaunchEntity, isDraftStatus } from 'utils/entityLifecycleStatus';
import { parseApiCalendarDate, toApiCalendarDate } from 'utils/calendarDate';

const VERSION_STATUSES = ['draft', 'scheduled', 'active', 'retired', 'cancelled'];

const JOI_REQUIRED_ERROR_TYPES = new Set(['any.required', 'string.empty', 'date.base', 'object.base']);

const useStyles = makeStyles(theme => ({
  actions: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  lifecycleActions: {
    display: 'flex',
    alignItems: 'center',
  },
  formActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: 'auto',
  },
  tooltip: {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    maxWidth: 320,
    borderRadius: theme.spacing(1),
  },
}));

const validate = values => {
  const schema = Joi.object({
    versionCode: Joi.string().trim().min(1).max(64).required(),
    revision: Joi.string().trim().min(1).max(64).required(),
    effectiveFrom: Joi.date().required(),
    effectiveTo: Joi.date().allow(null, '').optional(),
    status: Joi.object({
      value: Joi.string()
        .valid(...VERSION_STATUSES)
        .required(),
      label: Joi.string().required(),
    }).optional(),
  });

  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) {
    if (
      values.effectiveTo &&
      values.effectiveFrom &&
      new Date(values.effectiveTo).getTime() < new Date(values.effectiveFrom).getTime()
    ) {
      return { effectiveTo: i18next.t('jobTaskVersion.effectiveToBeforeFrom') };
    }
    return {};
  }

  return error.details.reduce((errors, detail) => {
    const field = detail.path[0] === 'status' ? 'status' : detail.path[0];
    if (JOI_REQUIRED_ERROR_TYPES.has(detail.type)) {
      errors[field] = i18next.t('errors.fieldIsRequired');
      return errors;
    }
    errors[field] = detail.type;
    return errors;
  }, {});
};

const JobTaskVersionForm = ({
  mode,
  initialValues,
  onSubmitForm,
  onLaunch,
  onCancelEntity,
  onCancel,
  submitting,
  canLaunch = true,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const statusOptions = VERSION_STATUSES.map(value => ({
    value,
    label: t(`jobTaskVersions.statuses.${value}`),
  }));

  const defaultStatus = statusOptions.find(option => option.value === 'draft') || statusOptions[0];
  const currentStatus = initialValues?.status || 'draft';
  const isDraft = isDraftStatus(currentStatus);
  const canEditEffectiveFrom = mode === 'create' || isDraft;
  const canEditEffectiveTo =
    mode === 'create' || isDraft || Boolean(initialValues?.canEditEffectiveTo);
  const canCancelVersion =
    initialValues?.canCancel !== undefined
      ? Boolean(initialValues.canCancel)
      : Boolean(initialValues?.status) &&
        !isDraft &&
        initialValues?.status !== 'cancelled' &&
        !initialValues?.hasNext;
  const showLaunch = mode === 'edit' && canLaunchEntity(currentStatus) && Boolean(onLaunch);
  const showCancelEntity = mode === 'edit' && canCancelVersion && Boolean(onCancelEntity);
  const isLaunchDisabled = !canLaunch;

  const formInitialValues = {
    versionCode: initialValues?.versionCode || '',
    revision: initialValues?.revision || '',
    effectiveFrom: parseApiCalendarDate(initialValues?.effectiveFrom) || new Date(),
    effectiveTo: parseApiCalendarDate(initialValues?.effectiveTo),
    status: statusOptions.find(option => option.value === initialValues?.status) || defaultStatus,
  };

  const handleSubmit = async values => {
    const payload = {
      versionCode: values.versionCode.trim(),
      revision: String(values.revision || '').trim(),
      effectiveFrom: toApiCalendarDate(values.effectiveFrom),
      effectiveTo: toApiCalendarDate(values.effectiveTo),
    };

    if (mode === 'create') {
      payload.status = 'draft';
    }

    const result = await onSubmitForm(payload);
    if (result?.error) {
      return {
        [FORM_ERROR]: resolveApiErrorMessage(result.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      };
    }
    return undefined;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={formInitialValues}
      validate={validate}
      render={({ handleSubmit: submitForm, submitting: formSubmitting, submitError, submitFailed }) => {
        const isBusy = submitting || formSubmitting;

        return (
          <form onSubmit={submitForm}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="versionCode"
                  id="versionCode"
                  label={t('tablesLabels.versionCode')}
                  placeholder={t('tablesLabels.versionCode')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  type="text"
                  name="revision"
                  id="revision"
                  label={t('trainingParts.revision', 'Revision')}
                  placeholder={t('trainingParts.revision', 'Revision')}
                  component={CustomTextField}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="effectiveFrom"
                  id="effectiveFrom"
                  label={t('tablesLabels.effectiveFrom')}
                  component={CustomDatePicker}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || !canEditEffectiveFrom}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="effectiveTo"
                  id="effectiveTo"
                  label={t('tablesLabels.effectiveTo')}
                  component={CustomDatePicker}
                  variant="outlined"
                  fullWidth
                  disabled={isBusy || !canEditEffectiveTo}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="status"
                  id="status"
                  label={t('tablesLabels.status')}
                  component={CustomSelect}
                  options={statusOptions}
                  variant="outlined"
                  fullWidth
                  isDisabled
                />
              </Grid>
            </Grid>

            {submitFailed && submitError && <SubmitErrorMessage message={submitError} />}

            <Box className={classes.actions}>
              {(showLaunch || showCancelEntity) && (
                <Box className={classes.lifecycleActions}>
                  {showCancelEntity && (
                    <Button variant="outlined" disabled={isBusy} onClick={() => onCancelEntity()}>
                      {t('jobTaskVersions.cancelEntity', 'Mark as cancelled')}
                    </Button>
                  )}
                  {showLaunch && (
                    <Tooltip
                      title={isLaunchDisabled ? t('jobTaskVersion.launchRequiresModules') : ''}
                      classes={{ tooltip: classes.tooltip }}
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={isBusy || isLaunchDisabled}
                          onClick={() => onLaunch()}
                        >
                          {t('jobTaskVersions.launch', 'Launch version')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              )}
              <Box className={classes.formActions}>
                <Button variant="outlined" onClick={onCancel} disabled={isBusy}>
                  {t('buttons.cancel')}
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={isBusy}>
                  {mode === 'create' ? t('jobTaskVersions.actionAdd') : t('buttons.save')}
                </Button>
              </Box>
            </Box>
          </form>
        );
      }}
    />
  );
};

JobTaskVersionForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  initialValues: PropTypes.shape({
    versionCode: PropTypes.string,
    revision: PropTypes.string,
    effectiveFrom: PropTypes.string,
    effectiveTo: PropTypes.string,
    status: PropTypes.string,
    hasNext: PropTypes.bool,
    canCancel: PropTypes.bool,
    canEditEffectiveTo: PropTypes.bool,
  }),
  onSubmitForm: PropTypes.func.isRequired,
  onLaunch: PropTypes.func,
  onCancelEntity: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  canLaunch: PropTypes.bool,
};

JobTaskVersionForm.defaultProps = {
  initialValues: null,
  onLaunch: null,
  onCancelEntity: null,
  submitting: false,
  canLaunch: true,
};

export default JobTaskVersionForm;
